import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Download } from "lucide-react";
import { formatCurrency, formatDate, isOfflinePlatform } from "shared";
import { toast } from "sonner";
import { IconText } from "@/components/icon-text";
import { RenderCell } from "@/components/table/cells/render-cell";
import { Button } from "@/components/ui/button";
import { SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { UserAvatar } from "@/components/user-avatar";
import { trpc } from "@/lib/trpcClient";
import { downloadExport } from "./--components/sync-action";

export const Route = createFileRoute(
  "/_authed/_org/accounting/sync-history/$id",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const { data: sync } = useQuery(
    trpc.accounting.syncDetails.queryOptions({ id }),
  );

  const { data: accounting } = useQuery(trpc.accounting.get.queryOptions());

  return (
    <div className="flex h-full min-w-lg flex-col">
      <SheetHeader className="border-border border-b p-6 pb-4">
        <SheetTitle className="flex items-center gap-3">
          <Link to="/accounting/sync-history">
            <ArrowLeft className="size-6 text-neutral-600" />
          </Link>

          {sync ? (
            formatDate(sync!.createdAt)
          ) : (
            <Skeleton className="h-8 w-56" />
          )}
        </SheetTitle>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            Synced by{" "}
            {sync ? (
              <UserAvatar userId={sync.createdBy} />
            ) : (
              <Skeleton className="h-6 w-56" />
            )}
          </div>

          {isOfflinePlatform(accounting?.type) && (
            <Button
              variant="outline"
              onClick={async () => {
                const count = sync?.transactions.length;

                await toast.promise(
                  downloadExport(
                    sync?.transactions.map((t) => t.id),
                    sync?.createdAt,
                  ),
                  {
                    error:
                      "Error exporting transactions. Please contact support or try again later.",
                    loading: "Preparing export...",
                    success: `Successfully exported ${count} transaction${count !== 1 ? "s" : ""}`,
                  },
                );
              }}
            >
              <Download />
              Download Export
            </Button>
          )}
        </div>
      </SheetHeader>

      {sync && (
        <div className="flex flex-col overflow-y-auto p-6">
          {sync.transactions.map((trx) => (
            <Link
              to="/transactions/card/$id"
              params={{ id: trx.id }}
              key={trx.id}
              className="group flex h-20 shrink-0 items-center justify-between border-border border-b px-2 hover:bg-neutral-50"
            >
              <RenderCell
                type="merchant"
                merchant={trx.merchant}
                merchantCategory={trx.category}
              />

              <div className="flex flex-col items-end">
                <span className="font-medium text-lg text-neutral-800">
                  {formatCurrency(trx.amountCents)}
                </span>

                <IconText
                  icon="arrow-up-right"
                  className="text-sm underline-offset-2 group-hover:text-neutral-600! group-hover:underline"
                  iconAfter
                >
                  View Transaction
                </IconText>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
